import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export interface ScoreInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    className?: string;
    placeholder?: string;
    style?: React.CSSProperties;
    step?: string;
    min?: number;
    max?: number;
    disabled?: boolean;
}

export const ScoreInput = ({ value, onChange, className, placeholder, style, step, min, max, disabled }: ScoreInputProps) => {
    const [localValue, setLocalValue] = useState(value !== null ? value.toString() : '');

    useEffect(() => {
        if (value === null) {
            // Always clear local value when external value is null
            setLocalValue('');
        } else {
            const parsed = parseFloat(localValue);
            // Sync if external value changed significantly (e.g. from slider)
            if (value !== parsed && !isNaN(value)) {
                setLocalValue(value.toString());
            }
        }
    }, [value, localValue]);

    const handleUpdate = (newVal: number) => {
        setLocalValue(newVal.toString());
        onChange(newVal);
    }

    const increment = () => {
        const current = parseFloat(localValue) || 0;
        const s = parseFloat(step || "0.1");
        // Fix floating point precision
        const next = Math.round((current + s) * 100) / 100;

        if (max !== undefined && next > max) {
            handleUpdate(max);
        } else {
            handleUpdate(next);
        }
    };

    const decrement = () => {
        const current = parseFloat(localValue) || 0;
        const s = parseFloat(step || "0.1");
        const next = Math.round((current - s) * 100) / 100;

        if (min !== undefined && next < min) {
            handleUpdate(min);
        } else {
            handleUpdate(next);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setLocalValue(val);

        if (val === '') {
            onChange(null);
            return;
        }

        const num = parseFloat(val);
        if (!isNaN(num)) {
            if (max !== undefined && num > max) onChange(max);
            else if (min !== undefined && num < min) onChange(min);
            else onChange(num);
        }
    };

    return (
        <div className={`relative group flex items-center ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <input
                type="number"
                min={min}
                max={max}
                step={step}
                value={localValue}
                onChange={handleChange}
                disabled={disabled}
                className={`${className} pr-6`}
                placeholder={placeholder}
                style={style}
            />

            {/* Custom Stepper Controls */}
            <div className="absolute right-1 flex flex-col gap-[1px]">
                <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: '#f1f5f9' }}
                    whileTap={{ scale: 0.97, transition: { type: 'spring', stiffness: 400, damping: 25 } }}
                    onClick={increment}
                    disabled={disabled}
                    className="p-0.5 rounded text-slate-400 hover:text-violet-500 transition-colors disabled:opacity-50"
                >
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.5 7.5L6 4L9.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </motion.button>
                <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: '#f1f5f9' }}
                    whileTap={{ scale: 0.97, transition: { type: 'spring', stiffness: 400, damping: 25 } }}
                    onClick={decrement}
                    disabled={disabled}
                    className="p-0.5 rounded text-slate-400 hover:text-violet-500 transition-colors disabled:opacity-50"
                >
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.5 4.5L6 8L2.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </motion.button>
            </div>
        </div>
    );
};
